import React, { useEffect, Suspense } from 'react';
import styled from 'styled-components';
import {
  ActionButton,
  Label,
  TextInput,
  DateInputV4,
  IconActionChevronLeft,
} from 'wg-fe-ui';
import * as Yup from 'yup';
import i18n from '../i18n';

import { string } from '../constants/validationSchemas.js';
import useForm from '../hooks/useForm';
import { useHistory } from 'react-router';

const FlowParentalInformationForm = ({ handleFormValues, defaultValues }) => {
  const history = useHistory();
  const SignupSchema = Yup.object().shape({
    RecentHba1cTest: Yup.date()
      .required(i18n.t('Required'))
      .max(new Date(), i18n.t('Date must be in the past')),
    ResultHba1c: string.required,
  });

  const { handleChange, errors, handleSubmit, values } = useForm({
    validationSchema: SignupSchema,
    change: () => {},
  });

  useEffect(() => {
    defaultValuesHandler(defaultValues);
  }, [defaultValues]);

  function defaultValuesHandler() {
    Object.keys(defaultValues || {}).forEach((name) => {
      handleChange({ name, value: defaultValues[name] });
    });
  }

  const formSubmitHandler = (e) => {
    e.preventDefault();
    handleSubmit(handleFormValues);
  };

  function goBack(e) {
    e.preventDefault();
    history.goBack();
  }

  return (
    <Form onSubmit={formSubmitHandler}>
      <Suspense fallback={<div>Loading...</div>}>
        <CatTitle>{i18n.t('Health information')} </CatTitle>
        <FlexWrapper>
          <DateInputV4
            name="RecentHba1cTest"
            error={errors.RecentHba1cTest}
            onChange={handleChange}
            value={values.RecentHba1cTest}
          >
            <StyledLabel>
              {i18n.t('When was your last HBA1C test')}? *
            </StyledLabel>
          </DateInputV4>
          <StyledTextInput
            name="ResultHba1c"
            error={errors.ResultHba1c}
            onChange={handleChange}
            value={values.ResultHba1c}
          >
            <StyledLabel>{i18n.t('HBA1C test result')} *</StyledLabel>
          </StyledTextInput>
        </FlexWrapper>
        <Required>* {i18n.t('Required fields')}</Required>
        <ButtonContainer>
          <BackButton type="button" name="back" onClick={goBack}>
            <IconActionChevronLeft />
            {i18n.t('Back')}
          </BackButton>
          <StyledActionButton
            type="submit"
            value="Submit"
            data-test-id="drivers_information_submit"
          >
            {i18n.t('Next')}
          </StyledActionButton>
        </ButtonContainer>
      </Suspense>
    </Form>
  );
};

const BackButton = styled.button`
  align-items: center;
  background-color: white;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  font-size: 1.6rem;
  justify-content: center;
  margin-right: 1rem;
  padding-right: 1.75rem;

  &:hover {
    background-color: ${({ theme }) => theme.brand.light};
    color: white;
    svg > path {
      stroke: white;
    }
  }
  > svg {
    height: 2.5rem;
    margin-bottom: 0.35rem;
  }
`;

const Required = styled.p`
  font-size: 1.4rem;
  color: ${({ theme }) => theme.typo.label};
  margin-top: 0;
  @media (max-width: 768px) {
    margin: 0 2rem 2rem 2rem;
  }
`;

const StyledActionButton = styled(ActionButton)`
  color: white;
  background-color: default;
  font-weight: normal;
`;

const FlexWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  height: 9rem;

  & > div + div {
    margin-left: 1rem;
  }
  @media (max-width: 768px) {
    height: auto;
    flex-direction: column;
    margin: 0;
    & > div + div {
      margin-left: 0rem;
    }
  }
`;

const StyledLabel = styled(Label)`
  color: ${({ theme }) => theme.typo.text};
  a {
    margin-top: -0.55rem;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const ButtonContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;

  & button {
    font-weight: 550;
    height: 5rem;
    min-width: 12rem;
    width: 12rem;
    &:focus {
      outline: 0;
    }
  }
`;

const StyledTextInput = styled(TextInput)`
  height: 9rem;
  p.valueType {
    padding: 1.4rem;
    border-left: 1px solid #cccccc;
    position: absolute;
    margin: 0;
    right: 0rem;
    top: 2.5rem;
  }
`;

const CatTitle = styled.h1`
  font-weight: 900;
  font-size: 1.8rem;
  flex-shrink: 0;
  line-height: 2.5rem;
  margin-bottom: 3rem;
  color: ${({ theme }) => theme.brand.primary};
`;

export default FlowParentalInformationForm;
